import React, { useState, useEffect } from 'react';
import { BreadCrumb } from 'primereact/breadcrumb';
import { useLocation, useParams } from 'react-router-dom';
import './app-breadcrumbs.css';
import { MenuItem } from 'primereact/menuitem';
import globalSettingsBranches from '../../stores/global-settings-branches';
import globalProgramStore from '../../stores/program-store';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';
import { Tooltip } from 'primereact/tooltip';
import globalUserProfile from '../../stores/global-user-profile';

/**
 * Returns the appropriate Hebrew greeting based on the current time.
 * Returns null between 00:00-06:00 (no greeting should be displayed).
 */
const getTimeBasedGreeting = (): string | null => {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const timeInMinutes = hours * 60 + minutes;

  // 00:00 - 06:00 (0 to 360 minutes) - no greeting
  if (timeInMinutes < 360) {
    return null;
  }
  // 06:00 - 11:30 (360 to 690 minutes) - בוקר טוב
  if (timeInMinutes < 690) {
    return 'בוקר טוב';
  }
  // 11:30 - 15:00 (690 to 900 minutes) - צהריים טובים
  if (timeInMinutes < 900) {
    return 'צהריים טובים';
  }
  // 15:00 - 17:30 (900 to 1050 minutes) - אחר הצהריים טובים
  if (timeInMinutes < 1050) {
    return 'אחר הצהריים טובים';
  }
  // 17:30 - 24:00 (1050 to 1440 minutes) - ערב טוב
  return 'ערב טוב';
};

interface BreadcrumbConfig {
  label: string;
  icon?: string;
  children?: { [key: string]: BreadcrumbConfig };
}

const routeConfig: { [key: string]: BreadcrumbConfig } = {
  education: {
    label: 'חינוך',
    icon: 'pi pi-graduation-cap',
    children: {
      lectures: { label: 'מפגשים', icon: 'pi pi-calendar' },
    },
  },
  reports: {
    label: 'דוחות',
    icon: 'pi pi-chart-bar',
    children: {
      attendance: { label: 'דוח נוכחות כללית' },
      dropout: { label: 'דוח נשירה' },
      exams: { label: 'דוח מבחנים' },
      assignments: { label: 'דוח מטלות' },
      lectures: { label: 'דוח מפגשים שלא נסגרו בהצלחה' },
      'students-course-status': { label: 'דוח סטטוס תלמידים בקורס' },
      elementary: { label: 'דוח יסודי' },
      'tuition-payments': { label: 'דוח תשלומי שכר לימוד' },
      checks: { label: 'דוח המחאות' },
      'standing-orders': { label: 'דוח הוראות קבע' },
      finance: {
        label: 'דוחות כספיים',
        children: {
          'courses-exceeding-hours': { label: 'דוח חריגת שעות בקורסים' },
        },
      },
    },
  },
  settings: {
    label: 'הגדרות',
    icon: 'pi pi-cog',
    children: {
      studies: { label: 'הגדרות לימודים' },
      students: { label: 'הגדרות תלמידים' },
      lectures: { label: 'הגדרות מרצים' },
      users: { label: 'הגדרות משתמשים' },
      operation: { label: 'שרותים פיננסים' },
      times: { label: 'ניהול זמנים' },
      docs: { label: 'ניהול מסמכים' },
      'branch-and-classes': {
        label: 'שלוחות וכיתות',
        children: {
          branches: { label: 'שלוחות' },
          buildings: { label: 'מבנים' },
          classes: { label: 'כיתות' },
        },
      },
    },
  },
  users: { label: 'משתמשים', icon: 'pi pi-users' },
  students: { label: 'תלמידים', icon: 'pi pi-users' },
  lecturers: { label: 'מרצים', icon: 'pi pi-users' },
  administration: { label: 'מנהלה', icon: 'pi pi-users' },
  programs: { label: 'תכניות', icon: 'pi pi-book' },
  courses: { label: 'קורסים', icon: 'pi pi-bookmark' },
  'courses-list': { label: 'רשימת קורסים', icon: 'pi pi-list' },
  accountant: { label: 'הנהלת חשבונות', icon: 'pi pi-dollar' },
  meetings: { label: 'מפגשים', icon: 'pi pi-calendar' },
  exams: { label: 'מבחנים', icon: 'pi pi-file' },
  feedbacks: { label: 'משובים', icon: 'pi pi-comment' },
  'payment-services': { label: 'שירותי תשלום', icon: 'pi pi-credit-card' },
  lectures: { label: 'הרצאות', icon: 'pi pi-video' },
};

export const AppBreadcrumbs = observer(() => {
  const location = useLocation();
  const { programId } = useParams();
  const [greeting, setGreeting] = useState<string | null>(getTimeBasedGreeting);

  // Update greeting every minute to handle time transitions
  useEffect(() => {
    const updateGreeting = () => {
      setGreeting(getTimeBasedGreeting());
    };

    // Update immediately
    updateGreeting();

    // Set up interval to check every minute
    const intervalId = setInterval(updateGreeting, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const firstName =
    globalUserProfile.userProfile?.userWithRole?.firstName || '';
  const lastName = globalUserProfile.userProfile?.userWithRole?.lastName || '';

  const generateBreadcrumbItems = (): MenuItem[] => {
    const items: MenuItem[] = [];
    const pathSegments = location.pathname.split('/').filter(Boolean);

    // Handle education routes
    if (pathSegments[0] === 'education' && pathSegments[1] === 'lectures') {
      items.push({
        label: 'מפגשים',
        url: '/education/lectures',
        icon: 'pi pi-calendar',
      });
    }

    // Handle program routes
    if (pathSegments[0] === 'campuses' && pathSegments[2] === 'programs') {
      items.push({
        label: 'תכניות',
        url: '/programs',
        icon: 'pi pi-book',
      });

      // Add current program name if we're in a specific program
      if (programId) {
        const currentProgram = globalProgramStore.programs.get(
          Number(programId),
        );
        if (currentProgram) {
          items.push({
            label: `${currentProgram.metadata.programName} (${currentProgram.metadata.id})`,
            url: `/campuses/${currentProgram.metadata.branchId}/programs/${programId}`,
          });
          // Add courses breadcrumb if we're in the courses section
          if (pathSegments[4] === 'courses') {
            items.push({
              label: 'קורסים',
              url: `/campuses/${currentProgram.metadata.branchId}/programs/${programId}`,
              icon: 'pi pi-bookmark',
            });

            // Add course ID if we're viewing a specific course
            if (pathSegments[3]) {
              items.push({
                label: `${pathSegments[5]}`,
                url: `/campuses/${currentProgram.metadata.branchId}/programs/${programId}/courses/${pathSegments[5]}`,
              });
            }
          }
        }
      }
    }

    // Handle courses-list route
    if (pathSegments[0] === 'courses-list') {
      items.push({
        label: 'קורסים',
        url: '/courses-list',
        icon: 'pi pi-bookmark',
      });
    }

    // Add settings if we're in settings section
    if (pathSegments[0] === 'settings') {
      // Always add הגדרות first
      items.push({
        label: 'הגדרות',
        url: '/settings',
        icon: 'pi pi-cog',
      });

      // Handle studies route
      if (pathSegments[1] === 'studies') {
        items.push({
          label: 'לימודים',
          url: '/settings/studies',
          icon: 'pi pi-cog',
        });
      }

      // Handle lectures route
      if (pathSegments[1] === 'lectures') {
        items.push({
          label: 'מרצים',
          url: '/settings/lectures',
          icon: 'pi pi-cog',
        });
      }

      // Handle operation route
      if (pathSegments[1] === 'operation') {
        items.push({
          label: 'שירותים פיננסים',
          url: '/settings/operation',
          icon: 'pi pi-cog',
        });
      }

      // Handle docs route
      if (pathSegments[1] === 'docs') {
        items.push({
          label: 'מסמכים',
          url: '/settings/docs',
          icon: 'pi pi-cog',
        });
      }

      // Handle times route
      if (pathSegments[1] === 'times') {
        items.push({
          label: 'מנהל זמנים',
          url: '/settings/times',
          icon: 'pi pi-cog',
        });
      }

      // Handle buildings route
      if (pathSegments[1] === 'buildings') {
        items.push({
          label: 'מבנים',
          url: '/settings/buildings',
          icon: 'pi pi-building',
        });

        // Add current building name if we're in classes
        const buildingId = pathSegments[2];
        if (buildingId) {
          const currentBuilding = globalSettingsBranches.buildings.find(
            (b) => b.id === Number(buildingId),
          );
          if (currentBuilding) {
            items.push({
              label: currentBuilding.buildingName,
              url: `/settings/buildings/${buildingId}/classes`,
            });
          }
        }
      }
    }

    // Handle branch hierarchy
    if (pathSegments.includes('branch-and-classes')) {
      items.push({
        label: 'שלוחות וכיתות',
        url: '/settings/branch-and-classes',
      });

      // Add current branch name if we're in buildings or classes
      const currentBranch = globalSettingsBranches.getCurrentBranch();
      if (
        currentBranch &&
        (pathSegments.includes('buildings') || pathSegments.includes('classes'))
      ) {
        items.push({
          label: currentBranch.branchName,
          url: `/settings/branch-and-classes/branches/${currentBranch.id}/buildings`,
        });

        // Add current building name if we're in classes
        if (pathSegments.includes('classes')) {
          const currentBuilding = globalSettingsBranches.getCurrentBuilding();
          if (currentBuilding) {
            items.push({
              label: currentBuilding.buildingName,
              url: `/settings/branch-and-classes/branches/${currentBranch.id}/buildings/${currentBuilding.id}/classes`,
            });
          }
        }
      }
    }

    return items;
  };

  const breadcrumbHome = { icon: 'pi pi-home', url: '/' };
  const items = generateBreadcrumbItems();

  // Don't show breadcrumbs on login or home page
  if (location.pathname === '/login' || location.pathname === '/') {
    return null;
  }

  const handleOpenInquiryPage = () => {
    window.open(
      'https://docs.google.com/forms/d/e/1FAIpQLSfuwWGCk2Gw4nB5NEOg-qb42zbm5bCBwDA2NMXaTBiHZrF2YQ/viewform?usp=dialog',
      '_blank',
    );
  };

  return (
    <div style={{ height: '48px' }} className="app-breadcrumbs">
      <BreadCrumb
        model={items}
        home={breadcrumbHome}
        className="custom-breadcrumb"
      />
      <div className="greeting-container">
        {greeting && firstName && (
          <span className="greeting-text">
            {greeting} {firstName} {lastName}
          </span>
        )}
        <Tooltip target=".inquiry-icon" position="bottom" />
        <i
          className="pi pi-envelope inquiry-icon"
          data-pr-tooltip="השארת פנייה"
          onClick={handleOpenInquiryPage}
        />
      </div>
    </div>
  );
});
